/**
 * PKCE (Proof Key for Code Exchange) Client Utilities
 * Implements RFC 7636 for OAuth2 Authorization Code flow in SPAs
 *
 * Flow:
 *   1. Generate code_verifier (random, high-entropy string)
 *   2. Derive code_challenge = base64url(sha256(code_verifier))
 *   3. Store both + state in sessionStorage (survive redirect)
 *   4. After redirect, retrieve and clear them
 */

// ── Storage Keys ──
const PKCE_VERIFIER_KEY = 'pkce_code_verifier';
const PKCE_STATE_KEY = 'pkce_state';

// ── Crypto Helpers ──

/**
 * Base64url-encode a Uint8Array (RFC 4648 §5, no padding).
 */
function base64urlEncode(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Generate a cryptographically random code_verifier.
 * RFC 7636 §4.1: 43–128 characters from [A-Z, a-z, 0-9, "-", ".", "_", "~"]
 * We generate 64 random bytes -> 86-char base64url string.
 */
export function generateCodeVerifier() {
  const array = new Uint8Array(64);
  crypto.getRandomValues(array);
  return base64urlEncode(array);
}

/**
 * Derive the code_challenge from a code_verifier using S256.
 * code_challenge = BASE64URL(SHA256(code_verifier))
 */
export async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64urlEncode(new Uint8Array(digest));
}

/**
 * Generate a random state parameter for CSRF protection.
 * 32 random bytes -> 43-char base64url string.
 */
export function generateState() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64urlEncode(array);
}

// ── SessionStorage Persistence ──

/**
 * Store PKCE parameters in sessionStorage before redirect.
 */
export function storePKCEParams(codeVerifier, state) {
  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
  sessionStorage.setItem(PKCE_STATE_KEY, state);
}

/**
 * Retrieve stored PKCE parameters after redirect.
 * Returns null if either value is missing.
 */
export function getPKCEParams() {
  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const state = sessionStorage.getItem(PKCE_STATE_KEY);

  if (!codeVerifier || !state) return null;

  return { codeVerifier, state };
}

/**
 * Clear stored PKCE parameters (after successful exchange or on error).
 */
export function clearPKCEParams() {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
}
